import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import api from '../services/api';
import { formatCpf, formatCep, normalizeCep } from '../common/validators';
import './PublicCrechePage.css';
import './CompletarCadastroMatriculaPage.css';

/** Mesmos campos do bloco "Responsável Legal e Financeiro" do cadastro da criança. */
interface FormCadastro {
  responsavel_nome: string;
  responsavel_parentesco: string;
  responsavel_cpf: string;
  responsavel_rg: string;
  responsavel_profissao: string;
  responsavel_telefone: string;
  responsavel_email: string;
  responsavel_nacionalidade: string;
  responsavel_estado_civil: string;
  responsavel_cep: string;
  responsavel_logradouro: string;
  responsavel_numero: string;
  responsavel_complemento: string;
  responsavel_bairro: string;
  responsavel_cidade: string;
  responsavel_estado: string;
}

interface DadosConvite {
  crianca_nome: string;
  creche_nome: string;
  turma_nome?: string | null;
  ano_letivo?: number;
  concluido: boolean;
  responsavel: Partial<FormCadastro>;
}

const VAZIO: FormCadastro = {
  responsavel_nome: '',
  responsavel_parentesco: '',
  responsavel_cpf: '',
  responsavel_rg: '',
  responsavel_profissao: '',
  responsavel_telefone: '',
  responsavel_email: '',
  responsavel_nacionalidade: 'Brasileira',
  responsavel_estado_civil: '',
  responsavel_cep: '',
  responsavel_logradouro: '',
  responsavel_numero: '',
  responsavel_complemento: '',
  responsavel_bairro: '',
  responsavel_cidade: '',
  responsavel_estado: '',
};

/** Só o que veio preenchido — não apaga os defaults do formulário. */
function apenasPreenchidos(obj: Partial<FormCadastro>): Partial<FormCadastro> {
  const saida: Record<string, string> = {};
  for (const [k, v] of Object.entries(obj)) {
    if (String(v ?? '').trim()) saida[k] = String(v);
  }
  return saida as Partial<FormCadastro>;
}

/**
 * Página pública do convite: a creche confirmou a vaga e o responsável finaliza
 * aqui o próprio cadastro. Sem login — o acesso é o token do link.
 */
export default function CompletarCadastroMatriculaPage() {
  const { token } = useParams<{ token: string }>();
  const [dados, setDados] = useState<DadosConvite | null>(null);
  const [erroCarregar, setErroCarregar] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [form, setForm] = useState<FormCadastro>(VAZIO);
  const [buscandoCep, setBuscandoCep] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState(false);

  useEffect(() => {
    if (!token) return;
    api
      .get<DadosConvite>(`/publico/matricula/${token}`)
      .then(({ data }) => {
        setDados(data);
        // Aproveita o que a creche já tem da pré-inscrição (nome, telefone, e-mail).
        setForm((prev) => ({ ...prev, ...apenasPreenchidos(data?.responsavel || {}) }));
        if (data?.concluido) setSucesso(true);
      })
      .catch((e) => setErroCarregar(e?.response?.data?.message || 'Link inválido ou expirado.'))
      .finally(() => setCarregando(false));
  }, [token]);

  function onChange(campo: keyof FormCadastro, valor: string) {
    setForm((prev) => ({ ...prev, [campo]: valor }));
  }

  async function buscarCep() {
    const cep = normalizeCep(form.responsavel_cep);
    if (cep.length !== 8) return;
    setBuscandoCep(true);
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await resp.json();
      if (data.erro) {
        setErro('CEP não encontrado — confira o número digitado.');
        return;
      }
      setForm((prev) => ({
        ...prev,
        responsavel_logradouro: data.logradouro || prev.responsavel_logradouro,
        responsavel_bairro: data.bairro || prev.responsavel_bairro,
        responsavel_cidade: data.localidade || prev.responsavel_cidade,
        responsavel_estado: data.uf || prev.responsavel_estado,
      }));
      setErro('');
    } catch {
      setErro('Não foi possível consultar o CEP agora — preencha o endereço à mão.');
    } finally {
      setBuscandoCep(false);
    }
  }

  async function enviar(e: React.FormEvent) {
    e.preventDefault();
    setErro('');
    setEnviando(true);
    try {
      await api.post(`/publico/matricula/${token}`, form);
      setSucesso(true);
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      setErro(Array.isArray(msg) ? msg.join(' ') : msg || 'Não foi possível enviar o cadastro.');
    } finally {
      setEnviando(false);
    }
  }

  if (carregando) return <div className="pc-loading">Carregando…</div>;

  if (erroCarregar) {
    return (
      <div className="pc-root">
        <div className="pc-nao-encontrada">
          <h1>Cadastro de matrícula</h1>
          <p>{erroCarregar}</p>
          <p style={{ color: '#64748b' }}>Fale com a creche para receber um novo link.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pc-root">
      <header className="pc-header">
        <h1>{dados?.creche_nome}</h1>
        <p className="pc-telefone">Finalização de matrícula</p>
      </header>

      <section className="pc-form-section" style={{ paddingTop: 32 }}>
        {sucesso ? (
          <div className="pc-sucesso">
            <h2>✅ Cadastro enviado!</h2>
            <p>
              Recebemos os seus dados. A creche vai confirmar a matrícula de{' '}
              <strong>{dados?.crianca_nome}</strong> e entrará em contato com os próximos passos.
            </p>
          </div>
        ) : (
          <form className="pc-form" onSubmit={enviar}>
            <div className="ccm-vaga">
              🎉 <strong>Há vaga para {dados?.crianca_nome}</strong>
              {dados?.turma_nome ? <> na turma <strong>{dados.turma_nome}</strong></> : null}
              {dados?.ano_letivo ? <> ({dados.ano_letivo})</> : null}. Para concluirmos a matrícula,
              preencha abaixo os dados do responsável legal e financeiro.
            </div>

            <p className="pc-form-section-title">Responsável legal e financeiro</p>
            <div className="pc-form-row">
              <label>Nome completo *
                <input value={form.responsavel_nome} onChange={(e) => onChange('responsavel_nome', e.target.value)} required />
              </label>
              <label>Parentesco / relação *
                <select value={form.responsavel_parentesco} onChange={(e) => onChange('responsavel_parentesco', e.target.value)} required>
                  <option value="">Selecione...</option>
                  <option value="Mãe">Mãe</option>
                  <option value="Pai">Pai</option>
                  <option value="Avô/Avó">Avô/Avó</option>
                  <option value="Tutor Legal">Tutor Legal</option>
                  <option value="Outro">Outro</option>
                </select>
              </label>
            </div>

            <div className="pc-form-row">
              <label>CPF *
                <input value={form.responsavel_cpf} onChange={(e) => onChange('responsavel_cpf', formatCpf(e.target.value))} placeholder="000.000.000-00" required />
              </label>
              <label>RG
                <input value={form.responsavel_rg} onChange={(e) => onChange('responsavel_rg', e.target.value)} />
              </label>
            </div>

            <div className="pc-form-row">
              <label>Profissão
                <input value={form.responsavel_profissao} onChange={(e) => onChange('responsavel_profissao', e.target.value)} />
              </label>
              <label>Telefone / WhatsApp *
                <input value={form.responsavel_telefone} onChange={(e) => onChange('responsavel_telefone', e.target.value)} required />
              </label>
            </div>

            <div className="pc-form-row">
              <label>E-mail *
                <input type="email" value={form.responsavel_email} onChange={(e) => onChange('responsavel_email', e.target.value)} required />
              </label>
              <label>Nacionalidade *
                <input value={form.responsavel_nacionalidade} onChange={(e) => onChange('responsavel_nacionalidade', e.target.value)} placeholder="Brasileira" required />
              </label>
            </div>

            <label>Estado civil *
              <select value={form.responsavel_estado_civil} onChange={(e) => onChange('responsavel_estado_civil', e.target.value)} required>
                <option value="">Selecione...</option>
                <option value="solteiro(a)">Solteiro(a)</option>
                <option value="casado(a)">Casado(a)</option>
                <option value="divorciado(a)">Divorciado(a)</option>
                <option value="viúvo(a)">Viúvo(a)</option>
                <option value="união estável">União estável</option>
              </select>
            </label>

            <p className="pc-form-section-title">Endereço do responsável</p>
            <p className="ccm-obs">Digite o CEP para preencher automaticamente; informe apenas número e complemento.</p>
            <div className="pc-form-row">
              <label>CEP *
                <input value={form.responsavel_cep} onChange={(e) => onChange('responsavel_cep', formatCep(e.target.value))} onBlur={buscarCep} placeholder="00000-000" required />
              </label>
              <label>{buscandoCep ? 'Buscando CEP…' : 'Logradouro *'}
                <input value={form.responsavel_logradouro} onChange={(e) => onChange('responsavel_logradouro', e.target.value)} required />
              </label>
            </div>
            <div className="ccm-row-3">
              <label>Número *
                <input value={form.responsavel_numero} onChange={(e) => onChange('responsavel_numero', e.target.value)} required />
              </label>
              <label>Complemento
                <input value={form.responsavel_complemento} onChange={(e) => onChange('responsavel_complemento', e.target.value)} />
              </label>
              <label>Bairro *
                <input value={form.responsavel_bairro} onChange={(e) => onChange('responsavel_bairro', e.target.value)} required />
              </label>
            </div>
            <div className="pc-form-row">
              <label>Cidade *
                <input value={form.responsavel_cidade} onChange={(e) => onChange('responsavel_cidade', e.target.value)} required />
              </label>
              <label>UF *
                <input value={form.responsavel_estado} maxLength={2} onChange={(e) => onChange('responsavel_estado', e.target.value.toUpperCase())} placeholder="SC" required />
              </label>
            </div>

            {erro && <div className="pc-erro">{erro}</div>}

            <button type="submit" className="pc-btn-enviar" disabled={enviando}>
              {enviando ? 'Enviando…' : 'Enviar cadastro'}
            </button>
            <p className="ccm-obs" style={{ marginTop: 12 }}>
              Seus dados são usados exclusivamente para a matrícula da criança e para o contrato de
              prestação de serviços, conforme a Lei Geral de Proteção de Dados (LGPD).
            </p>
          </form>
        )}
      </section>

      <footer className="pc-footer">
        <p>{dados?.creche_nome}</p>
      </footer>
    </div>
  );
}
